import { useCallback, useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import { Box, Stack, Typography } from '@mui/material';

import dayjs from 'dayjs';
import { formatDateToMonthYear } from 'features/user_admin/SpecialDateRequest/utils';
import { dimmed_white_color } from 'theme/cemTheme';

import FontAwesomeIcon from '../FontAwesomeIcon';
import YearMenu from './YearMenu';
import { diffInCalendarDays } from './utils';

import './DateRangePicker.css';

import styles from './DateTimeSelector.module.css';

interface IDateRangePicker {
  onChange?: (dates: [dayjs.Dayjs | null, dayjs.Dayjs | null]) => void;
  minDate?: dayjs.Dayjs;
  maxDate?: dayjs.Dayjs;
  startValue?: dayjs.Dayjs;
  endValue?: dayjs.Dayjs;
  openToDate?: dayjs.Dayjs;
  maxDays?: number; // limit selectable range in date picker to available quota
  minDays?: number;
  showYearSelector?: boolean;
  filterDate?: (date: Date) => boolean; // filter calendar days that may be selected
  vnrtOrRtDates?: Set<string>;
  nrtDates?: Set<string>;
  monthsShown?: number;
  selectRange?: boolean;
  extraWrapper?: boolean;
  showShiftCaption?: boolean;
}

export default function DateRangePicker({
  onChange,
  minDate,
  maxDate,
  startValue,
  endValue,
  openToDate,
  maxDays,
  minDays,
  showYearSelector = true,
  filterDate,
  vnrtOrRtDates,
  nrtDates,
  monthsShown = 2,
  selectRange = true,
  extraWrapper = true,
  showShiftCaption = false,
}: IDateRangePicker) {
  const [startDate, setStartDate] = useState<Date | null>(startValue?.toDate());
  const [endDate, setEndDate] = useState<Date | null>(endValue?.toDate());
  const [viewDate, setViewDate] = useState<Date>(
    () => openToDate?.toDate() ?? startValue?.toDate() ?? new Date(),
  );
  const [isShiftPressed, setIsShiftPressed] = useState(false);
  const [rangeError, setRangeError] = useState(false);
  const canSelectRange = selectRange || isShiftPressed;

  useEffect(() => {
    setStartDate(startValue?.toDate() ?? null);
    setEndDate(endValue?.toDate() ?? null);
  }, [startValue?.format('YYYY-MM-DD'), endValue?.format('YYYY-MM-DD')]);

  useEffect(() => {
    const next = openToDate?.toDate() ?? startValue?.toDate();
    if (next) setViewDate(next);
  }, [openToDate?.format('YYYY-MM-DD'), startValue?.format('YYYY-MM-DD')]);

  const updateDates = (start: dayjs.Dayjs | null, end: dayjs.Dayjs | null) => {
    setStartDate(start?.toDate() ?? null);
    setEndDate(end?.toDate() ?? null);
    if (start && !end && isShiftPressed) return;
    onChange?.([start, end]);
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Shift') setIsShiftPressed(true);
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Shift') setIsShiftPressed(false);
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  const updateYear = (y: number) => {
    let next = dayjs(viewDate).year(y);
    if (maxDate && next.isAfter(maxDate)) next = maxDate;
    if (minDate && next.isBefore(minDate)) next = minDate;
    setViewDate(next.toDate());
  };

  const dayClassName = useCallback(
    (date: Date) => {
      const key = dayjs(date).format('YYYY-MM-DD');
      if (vnrtOrRtDates?.has(key)) return 'stl-day-rt';
      if (nrtDates?.has(key)) return 'stl-day-nrt';
      return undefined;
    },
    [vnrtOrRtDates, nrtDates],
  );

  const handleRangeChange = (dates: Date[]) => {
    if (!selectRange && !isShiftPressed) return;
    const [start_d, end_d] = dates;
    const start = start_d ? dayjs(start_d) : null;
    const end = end_d ? dayjs(end_d) : null;
    if (maxDays && minDays && start && end) {
      // validation on selected range
      const diff = diffInCalendarDays(start, end);
      if (diff > maxDays || diff < minDays) return;
      if (
        vnrtOrRtDates?.has(start.format('YYYY-MM-DD')) ||
        vnrtOrRtDates?.has(end.format('YYYY-MM-DD'))
      ) {
        return;
      }
      if (filterDate) {
        let current = start;
        while (current.isBefore(end, 'day') || current.isSame(end, 'day')) {
          if (!filterDate(current.toDate())) {
            setRangeError(true);
            setStartDate(startValue?.toDate() ?? null);
            setEndDate(endValue?.toDate() ?? null);
            return;
          }
          current = current.add(1, 'day');
        }
      }
    }

    setRangeError(false);
    updateDates(start, end);
  };

  // when shift-range, keep selection within one calendar month.
  const isSelectableDate = useCallback(
    (date: Date) => {
      if (filterDate && !filterDate(date)) return false;
      if (!selectRange && isShiftPressed && startDate && !endDate) {
        const start = dayjs(startDate);
        const candidate = dayjs(date);
        return (
          formatDateToMonthYear(candidate) === formatDateToMonthYear(start)
        );
      }
      return true;
    },
    [filterDate, selectRange, isShiftPressed, startDate, endDate],
  );

  //  when select range disabled and shift is not pressed, we want to update the start date only
  function handleSelect(date: Date) {
    if (!selectRange && date && !isShiftPressed) {
      setRangeError(false);
      updateDates(dayjs(date), null);
    }
  }

  return (
    <Stack className={extraWrapper ? styles.dateRangeWrapper : undefined}>
      {showYearSelector && (
        <YearMenu
          onSelect={updateYear}
          minDate={minDate}
          maxDate={maxDate}
          startDate={viewDate}
        />
      )}
      <DatePicker
        inline
        selected={startDate}
        openToDate={viewDate}
        monthsShown={monthsShown}
        minDate={minDate?.toDate()}
        maxDate={maxDate?.toDate()}
        startDate={startDate}
        endDate={endDate}
        filterDate={isSelectableDate}
        dayClassName={vnrtOrRtDates || nrtDates ? dayClassName : undefined}
        // enabling range selection on shift press
        {...(canSelectRange && {
          selectsRange: true,
          onChange: handleRangeChange,
        })}
        onSelect={handleSelect}
      />
      {showShiftCaption ? (
        <Box
          display="flex"
          alignItems="center"
          gap={0.5}
          m={'0.25rem'}
          key={Number(rangeError)}
        >
          <FontAwesomeIcon
            name={rangeError ? 'triangle-exclamation' : 'circle-info'}
            type="regular"
            size={'0.75rem'}
            sx={{ color: rangeError ? 'error.main' : dimmed_white_color }}
          />
          <Typography
            variant="caption"
            color={rangeError ? 'error' : dimmed_white_color}
          >
            {rangeError
              ? 'All dates must be available'
              : `Shift + Click to select up to ${maxDays} days`}
          </Typography>
        </Box>
      ) : null}
    </Stack>
  );
}
